import * as React from "react";
import { observer } from "mobx-react-lite";
import { TeamOutlined } from "@ant-design/icons";
import { Input, Select, Form, Collapse, Checkbox, Tooltip } from "antd";
import type { CheckboxChangeEvent } from "antd/es/checkbox";
import TaskStatusesVisualStore from "../stores/TaskStatusesVisualStore";
import { useForm } from "antd/lib/form/Form";
import { Utils } from "../../common/misc/Utils";
import AppModal from "../../common/components/AppModal";

type Props = {
    isSubtaskStatus: boolean;
    store: TaskStatusesVisualStore;
};
const FormItem = Form.Item;

const generateStatusCode = (name: string) =>
    name
        .trim()
        .replace(/[^a-zA-Z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "")
        .toUpperCase();

const TaskStatusCreateDialog: React.FC<Props> = ({
    isSubtaskStatus,
    store,
}) => {
    const [form] = useForm();
    const [isCodeEditedManually, setIsCodeEditedManually] =
        React.useState(false);
    const [activeCollapseKey, setActiveCollapseKey] = React.useState<string[]>(
        [],
    );

    React.useEffect(() => {
        if (store.newStatusDialogVisible) {
            form.setFieldsValue({
                accessUserIds: [],
                code: "",
                name: "",
                noRestrictions: true,
            });
            store.selectUsers([]);
            setIsCodeEditedManually(false);
            setActiveCollapseKey([]);
        }
    }, [form, store, store.newStatusDialogVisible]);

    if (!store.selectedTaskType) {
        return null;
    }

    const handleSubmit = async () => {
        form.validateFields()
            .then((values) => {
                store.selectUsers(values.accessUserIds ?? []);
                store.createTaskStatus(
                    values.name,
                    values.code,
                    isSubtaskStatus,
                );
                form.resetFields();
            })
            .catch((err) => {
                console.log(err);
            });
    };

    const handleValuesChange = (
        changedValues: Record<string, string | string[] | boolean | undefined>,
    ) => {
        if ("name" in changedValues) {
            const currentName = String(changedValues.name ?? "");
            const generatedCode = generateStatusCode(currentName);
            const currentCode = form.getFieldValue("code") ?? "";

            if (!isCodeEditedManually || currentCode === "") {
                form.setFieldValue("code", generatedCode);
                setIsCodeEditedManually(false);
            }
        }

        if ("code" in changedValues) {
            const currentCode = String(changedValues.code ?? "");
            const generatedCode = generateStatusCode(
                form.getFieldValue("name") ?? "",
            );
            setIsCodeEditedManually(
                currentCode !== "" && currentCode !== generatedCode,
            );
        }
    };

    const handleAccessUsersChange = (selectedUserIds: string[]) => {
        if (selectedUserIds.length > 0) {
            form.setFieldValue("noRestrictions", false);
        }
    };

    const handleNoRestrictionsChange = (event: CheckboxChangeEvent) => {
        if (event.target.checked) {
            form.setFieldValue("accessUserIds", []);
            store.selectUsers([]);
        }
    };

    const handleCancel = () => {
        store.setNewStatusDialogVisible(false);
        form.resetFields();
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const statusNameValidator = (rule: any, value: any, callback: any) => {
        const statusesToValidate = store!.taskStatuses
            .filter((s) => s.isSubtaskStatus === isSubtaskStatus)
            .map((t) => t.name.toLowerCase());
        if (value && statusesToValidate.includes(value.toLowerCase())) {
            callback("Status with the same name already exists");
        } else {
            callback();
        }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const statusCodeValidator = (rule: any, value: any, callback: any) => {
        const statusesToValidate = store!.taskStatuses
            .filter((s) => s.isSubtaskStatus === isSubtaskStatus)
            .map((t) => t.code?.toLowerCase());
        if (value && statusesToValidate.includes(value.toLowerCase())) {
            callback("Status with the same code already exists");
        } else {
            callback();
        }
    };

    return (
        <AppModal
            className="alpha-portal-dialog"
            title={
                isSubtaskStatus ? "Create subtask status" : "Create task status"
            }
            open={store.newStatusDialogVisible}
            closable={false}
            centered
            cancelButtonTitle="Cancel"
            onCancelClick={handleCancel}
            cancelButtonProps={{
                "data-id": "new-task-status-dialog-cancel",
            }}
            submitButtonTitle="Create"
            onSubmitClick={handleSubmit}
            submitButtonProps={{
                "data-id": "new-task-status-dialog-save",
                className: "dark",
            }}
            width={520}
        >
            <Form
                layout="vertical"
                data-id="create-task-type-dialog-form"
                form={form}
                initialValues={{
                    accessUserIds: [],
                    code: "",
                    name: "",
                    noRestrictions: true,
                }}
                className="alpha-form"
                onValuesChange={handleValuesChange}
            >
                <div className="app-modal__form-stack">
                    <FormItem
                        label={<span className="dialog-field-label">Name</span>}
                        name="name"
                        rules={[
                            {
                                required: true,
                                message: "Name is required",
                                whitespace: true,
                            },
                            { validator: statusNameValidator },
                        ]}
                    >
                        <Input data-id="input-Name" />
                    </FormItem>
                    <FormItem
                        label={<span className="dialog-field-label">Code</span>}
                        name="code"
                        rules={[{ validator: statusCodeValidator }]}
                    >
                        <Input data-id="input-code" />
                    </FormItem>
                    <Collapse
                        activeKey={activeCollapseKey}
                        bordered={false}
                        className="alpha-task-section-collapse"
                        expandIconPosition="end"
                        onChange={(keys) =>
                            setActiveCollapseKey(keys as string[])
                        }
                        items={[
                            {
                                key: "access",
                                collapsible: "disabled",
                                label: (
                                    <Tooltip
                                        title="Under construction"
                                        placement="bottom"
                                    >
                                        <div
                                            style={{
                                                alignItems: "center",
                                                display: "flex",
                                                gap: 14,
                                                paddingRight: 12,
                                            }}
                                        >
                                            <TeamOutlined
                                                style={{
                                                    color: "#475467",
                                                    fontSize: 16,
                                                }}
                                            />
                                            <div>
                                                <div
                                                    style={{
                                                        color: "#101828",
                                                        fontSize: 16,
                                                        fontWeight: 500,
                                                        lineHeight: "24px",
                                                    }}
                                                >
                                                    Access in this status
                                                    (optional)
                                                </div>
                                                <div
                                                    style={{
                                                        color: "#667085",
                                                        fontSize: 13,
                                                        lineHeight: "20px",
                                                        marginTop: 2,
                                                    }}
                                                >
                                                    Define which users can see
                                                    and interact with tasks in
                                                    this status.
                                                </div>
                                            </div>
                                        </div>
                                    </Tooltip>
                                ),
                                children: (
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 12,
                                        }}
                                    >
                                        <div
                                            style={{
                                                color: "#475467",
                                                fontSize: 14,
                                            }}
                                        >
                                            Users and roles
                                        </div>
                                        <FormItem
                                            name="accessUserIds"
                                            style={{ marginBottom: 0 }}
                                        >
                                            <Select
                                                mode="multiple"
                                                filterOption={
                                                    Utils.filterOption
                                                }
                                                getPopupContainer={(trigger) =>
                                                    trigger.parentElement ||
                                                    document.body
                                                }
                                                onChange={
                                                    handleAccessUsersChange
                                                }
                                                optionFilterProp="label"
                                                showSearch
                                                placeholder="Select users"
                                                options={
                                                    store.usersInProject
                                                        ? store.usersInProject.map(
                                                              (s) => ({
                                                                  label: s.userName,
                                                                  value: s.id,
                                                              }),
                                                          )
                                                        : []
                                                }
                                            />
                                        </FormItem>
                                        <div
                                            style={{
                                                color: "#667085",
                                            }}
                                        >
                                            Only selected users can see and
                                            interact with tasks while they are
                                            in this status.
                                        </div>
                                        <FormItem
                                            name="noRestrictions"
                                            valuePropName="checked"
                                            style={{ marginBottom: 0 }}
                                        >
                                            <Checkbox
                                                onChange={
                                                    handleNoRestrictionsChange
                                                }
                                            >
                                                <span
                                                    style={{
                                                        color: "#101828",
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    No restrictions
                                                </span>
                                            </Checkbox>
                                        </FormItem>
                                        <div
                                            style={{
                                                color: "#667085",
                                                marginLeft: 24,
                                            }}
                                        >
                                            All users can see and interact with
                                            tasks in this status.
                                        </div>
                                    </div>
                                ),
                                styles: {
                                    body: {
                                        border: "1px solid #d0d5dd",
                                        borderRadius: "0 0 12px 12px",
                                        borderTop: "none",
                                        padding: "16px 18px 18px",
                                    },
                                    header: {
                                        alignItems: "center",
                                        border: "1px solid #d0d5dd",
                                        borderRadius:
                                            activeCollapseKey.length > 0
                                                ? "12px 12px 0 0"
                                                : "12px",
                                        minHeight: 72,
                                        padding: "16px 18px",
                                    },
                                },
                            },
                        ]}
                    />
                </div>
            </Form>
        </AppModal>
    );
};

export default observer(TaskStatusCreateDialog);
